import { useState, useEffect } from 'react'

import { Divider, Menu, Modal, Text, TextInput, Tooltip } from '@mantine/core'
import { useMutation } from '@redwoodjs/web'
import { Check, ChevronsRight, Plus, MoreVertical, Pencil, Share2, Trash2, X, ArrowUpDown } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { DELETE_APPLICANT_FILTER_PRESET } from 'src/graphql/applicantFilterPresets.graphql'
import IconSearch from 'src/icons/IconSearch'
import { formatEnum } from 'src/lib/formatters'
import CustomTab from 'src/pages/ActivitiesPage/components/CustomTab'
import EditViewModal from 'src/pages/ApplicantsPage/components/EditViewModal/EditViewModal'
import ManageViewsModal from 'src/pages/ApplicantsPage/components/ManageViewsModal/ManageViewsModal'
import ShareViewModal from 'src/pages/ApplicantsPage/components/ShareViewModal/ShareViewModal'

const MAX_VISIBLE_ITEMS = 8

const FilterPresetList = ({
  defaultPresets = [],
  customPresets = [],
  onPresetClick,
  reset,
  selectedPresetFilter,
  numberOfApplicants,
  onAddNewView,
  refetch,
  hasUnsavedChanges = false,
  onSaveChanges,
  onDiscardChanges,
}) => {
  const [selectedTab, setSelectedTab] = useState('applicants')
  const [editPreset, setEditPreset] = useState(null)
  const [sharePreset, setSharePreset] = useState(null)
  const [manageViewsOpened, setManageViewsOpened] = useState(false)
  const [deletePreset, setDeletePreset] = useState(null)

  const [deleteFilterPreset, { loading: isDeleting }] = useMutation(
    DELETE_APPLICANT_FILTER_PRESET,
    {
      onCompleted: () => {
        if (selectedTab === deletePreset?.name) {
          setSelectedTab('applicants')
          reset()
        }
        refetch?.()
        toast('View deleted', 'success')
        setDeletePreset(null)
      },
      onError: () => {
        toast('Failed to delete view', 'error')
      },
    }
  )

  const handleDelete = async () => {
    if (!deletePreset?.applicantFilterPresetId) return
    await deleteFilterPreset({
      variables: {
        applicantFilterPresetId: deletePreset.applicantFilterPresetId,
      },
    })
  }

  // Update selectedTab when selectedPresetFilter changes
  useEffect(() => {
    if (selectedPresetFilter) {
      setSelectedTab(selectedPresetFilter)
    }
  }, [selectedPresetFilter])

  // Define the desired order of metric names
  const order = ['LEAD', 'APPLICATION', 'QUALIFICATION', 'ONBOARDING']

  // Helper function to check if a name is a default enum value
  const isDefaultEnumValue = (name: string) => {
    return order.some(
      (enumValue) => enumValue.toUpperCase() === name?.toUpperCase()
    )
  }

  // Helper function to format the display name
  // Only format default enum values, preserve custom preset names as-is
  const getDisplayName = (
    item: { name?: string; title?: string },
    isCustomPreset: boolean
  ) => {
    if (!item?.name) return item?.title || ''
    // If it's a custom preset or not a default enum value, return as-is
    if (isCustomPreset || !isDefaultEnumValue(item.name)) {
      return item.name
    }
    // Only format default enum values
    return formatEnum(item.name)
  }

  // Create a new array that includes all metrics with defaults
  const completeMetrics = order.map((metricName) => {
    // Find the metric in the props, if it exists
    const foundMetric = defaultPresets?.find(
      (metric) => metric?.name?.toUpperCase() === metricName.toUpperCase()
    )

    // Return the found metric or a default object with the name and value 0
    return foundMetric || { name: metricName, value: 0 }
  })

  // Combine customPresets and completeMetrics for scrolling
  const allItems = [...(customPresets || []), ...completeMetrics]
  // Remove duplicates by name (customPresets take precedence)
  const uniqueItems = allItems.filter(
    (item, idx, arr) =>
      arr.findIndex(
        (i) => i?.name?.toUpperCase() === item?.name?.toUpperCase()
      ) === idx
  )

  // Ensure selected tab is visible (last position) if not already visible
  const getVisibleAndDropdownItems = () => {
    // Find the currently selected item
    const selectedItem = uniqueItems.find((item) => item?.name === selectedTab)

    // Get default visible items (first MAX_VISIBLE_ITEMS)
    const defaultVisibleItems = uniqueItems.slice(0, MAX_VISIBLE_ITEMS)

    // Check if selected item is already visible
    const isSelectedItemVisible =
      selectedItem &&
      selectedTab !== 'applicants' &&
      defaultVisibleItems.some((item) => item?.name === selectedTab)

    if (
      selectedItem &&
      selectedTab !== 'applicants' &&
      !isSelectedItemVisible
    ) {
      // If selected item exists, is not "applicants", and not already visible,
      // move it to the last visible position (eighth position)
      const otherItems = uniqueItems.filter(
        (item) => item?.name !== selectedTab
      )
      const reorderedItems = [
        ...otherItems.slice(0, MAX_VISIBLE_ITEMS - 1),
        selectedItem,
        ...otherItems.slice(MAX_VISIBLE_ITEMS - 1),
      ]
      return {
        visibleItems: reorderedItems.slice(0, MAX_VISIBLE_ITEMS),
        dropdownItems: reorderedItems.slice(MAX_VISIBLE_ITEMS),
      }
    } else {
      // Default behavior - first MAX_VISIBLE_ITEMS items visible
      return {
        visibleItems: defaultVisibleItems,
        dropdownItems: uniqueItems.slice(MAX_VISIBLE_ITEMS),
      }
    }
  }

  const { visibleItems, dropdownItems } = getVisibleAndDropdownItems()
  const [search, setSearch] = useState('')

  return (
    <div className="flex w-full flex-row items-center gap-2 overflow-hidden">
      <div className="flex w-full flex-row items-center gap-2 overflow-hidden">
        <CustomTab
          selectedTab={selectedTab}
          value="applicants"
          variant="compact"
          label={
            <div className="flex flex-row items-center gap-1.5">
              All Applicants
              {selectedTab === 'applicants' && (
                <div className="text-[10px] text-doubleNickel-gray-500">
                  {numberOfApplicants || ''}
                </div>
              )}
            </div>
          }
          setSelectedTab={() => setSelectedTab('applicants')}
          onClick={() => reset()}
        />
        <Divider orientation="vertical" />
        <div className="mr-4 flex w-full flex-row items-center gap-2 overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {visibleItems.map((item, index) => {
            const isCustomPreset = (customPresets || []).some(
              (preset) => preset.applicantFilterPresetId === item.applicantFilterPresetId
            )
            const displayName = getDisplayName(item, isCustomPreset)
            return (
              <div key={index} className="flex items-center">
                <CustomTab
                  selectedTab={selectedTab}
                  value={item?.name}
                  variant="compact"
                  label={
                    <div className="flex flex-row items-center gap-1.5">
                      <div>{displayName}</div>
                      {selectedTab === item?.name && (
                        <div className="text-[10px] text-doubleNickel-gray-500">
                          {numberOfApplicants || ''}
                        </div>
                      )}
                      {selectedTab === item?.name && hasUnsavedChanges && isCustomPreset && (
                        <div className="ml-0.5 flex items-center gap-0.5">
                          <Tooltip label="Save changes">
                            <button
                              className="rounded p-0.5 text-doubleNickel-brand-500 hover:bg-doubleNickel-brand-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                onSaveChanges()
                              }}
                            >
                              <Check className="h-3 w-3" />
                            </button>
                          </Tooltip>
                          <Tooltip label="Discard changes">
                            <button
                              className="rounded p-0.5 text-doubleNickel-gray-400 hover:bg-doubleNickel-gray-100"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDiscardChanges()
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  }
                  setSelectedTab={() => setSelectedTab(item?.name)}
                  onClick={() => onPresetClick(item, isCustomPreset)}
                />
                {isCustomPreset && (
                  <Menu shadow="md" width={150} position="bottom-end" offset={4}>
                    <Menu.Target>
                      <button
                        title="View options"
                        className="ml-[-4px] rounded p-0.5 hover:bg-doubleNickel-gray-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-3 w-3 text-doubleNickel-gray-400" />
                      </button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<Pencil className="h-2.5 w-2.5" />}
                        className="text-xs"
                        onClick={() => setEditPreset(item)}
                      >
                        Rename
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<Share2 className="h-2.5 w-2.5" />}
                        className="text-xs"
                        onClick={() => setSharePreset(item)}
                      >
                        Share
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<Trash2 className="h-2.5 w-2.5" />}
                        className="text-xs"
                        onClick={() => setDeletePreset(item)}
                        color="red"
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </div>
            )
          })}
          <Menu shadow="md" width={250} position="bottom-start" offset={2}>
            <Menu.Target>
              <Tooltip label="More views">
                <DNButton variant="transparent">
                  <ChevronsRight className="h-4 w-4" />
                </DNButton>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <TextInput
                placeholder="Search views"
                leftSection={
                  <IconSearch className="h-3 w-3 fill-none stroke-doubleNickel-gray-700" />
                }
                size="xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Divider className="my-2" />
              <div className="max-h-[400px] overflow-y-auto">
                {dropdownItems.filter((item) =>
                  item?.name?.toLowerCase()?.includes(search.toLowerCase())
                ).length === 0 && (
                  <Menu.Label className="text-doubleNickel-gray-500">
                    No views found
                  </Menu.Label>
                )}

                {dropdownItems
                  .filter((item) =>
                    item?.name?.toLowerCase()?.includes(search.toLowerCase())
                  )
                  .map((item, index) => {
                    const isCustomPreset = (customPresets || []).some(
              (preset) => preset.applicantFilterPresetId === item.applicantFilterPresetId
            )
                    const displayName = getDisplayName(item, isCustomPreset)
                    return (
                      <Menu.Item
                        key={index}
                        onClick={() => {
                          setSelectedTab(item?.name)
                          onPresetClick(item, isCustomPreset)
                          setSearch('')
                        }}
                        className="flex w-full flex-row items-center gap-2 text-xs"
                      >
                        <div className="flex w-full flex-row items-center justify-between gap-2">
                          <span className="truncate">{displayName}</span>
                          {isCustomPreset && (
                            <Menu
                              shadow="md"
                              width={120}
                              position="right-start"
                              offset={4}
                            >
                              <Menu.Target>
                                <button
                                  className="rounded p-0.5 hover:bg-doubleNickel-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                  }}
                                >
                                  <MoreVertical className="h-3.5 w-3.5 text-doubleNickel-gray-500" />
                                </button>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<Pencil className="h-2.5 w-2.5" />}
                                  className="text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setEditPreset(item)
                                  }}
                                >
                                  Rename
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<Share2 className="h-2.5 w-2.5" />}
                                  className="text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSharePreset(item)
                                  }}
                                >
                                  Share
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<Trash2 className="h-2.5 w-2.5" />}
                                  className="text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDeletePreset(item)
                                  }}
                                  color="red"
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                        </div>
                      </Menu.Item>
                    )
                  })}
              </div>
              <Divider className="my-2" />
              {(customPresets || []).length > 1 && (
                <Menu.Item
                  className="flex flex-row items-center gap-2 text-doubleNickel-gray-700"
                  onClick={() => setManageViewsOpened(true)}
                >
                  <div className="flex flex-row items-center gap-2 text-xs">
                    <ArrowUpDown size={12} />
                    <div>Manage Views</div>
                  </div>
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
          <Tooltip label="Save current filters as a new view">
            <DNButton variant="transparent" onClick={onAddNewView}>
              <Plus className="h-4 w-4" />
            </DNButton>
          </Tooltip>
        </div>
      </div>

      {/* Edit View Modal */}
      {editPreset && (
        <EditViewModal
          opened={!!editPreset}
          onClose={(updatedName, wasDeleted) => {
            if (wasDeleted && selectedTab === editPreset?.name) {
              // If the currently selected view was deleted, reset to "All Applicants"
              setSelectedTab('applicants')
              reset()
            } else if (updatedName && selectedTab === editPreset?.name) {
              // If the currently selected view was renamed, update the selected tab
              setSelectedTab(updatedName)
            }
            setEditPreset(null)
          }}
          preset={editPreset}
          refetch={refetch}
          existingPresetNames={customPresets.map((preset) => preset.name)}
        />
      )}

      {/* Share View Modal */}
      {sharePreset && (
        <ShareViewModal
          opened={!!sharePreset}
          onClose={() => setSharePreset(null)}
          preset={sharePreset}
          refetch={refetch}
        />
      )}

      {/* Manage Views Modal */}
      {manageViewsOpened && (
        <ManageViewsModal
          opened={manageViewsOpened}
          onClose={() => setManageViewsOpened(false)}
          presets={customPresets || []}
          refetch={refetch}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletePreset && (
        <Modal
          opened={!!deletePreset}
          withCloseButton={false}
          onClose={() => setDeletePreset(null)}
          closeOnClickOutside={true}
          closeOnEscape={true}
          yOffset={100}
          size="sm"
          overlayProps={{
            backgroundOpacity: 0.2,
            blur: 5,
          }}
        >
          <Text className="text-sm text-doubleNickel-gray-700">
            Are you sure you want to delete &quot;{deletePreset.name}&quot;?
          </Text>
          <div className="mt-4 flex flex-row items-center justify-end gap-2">
            <DNButton
              variant="outline"
              className="h-7"
              onClick={() => setDeletePreset(null)}
            >
              Cancel
            </DNButton>
            <DNButton
              variant="error"
              className="h-7"
              loading={isDeleting}
              onClick={handleDelete}
            >
              Delete
            </DNButton>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default FilterPresetList
